import { useState } from "react";
import { Check, CaretUpDown } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { cn } from "../lib/utils";

const ItemLabel = ({ it, wrap }) => (
  <span className={cn("flex-1 min-w-0", wrap ? "[overflow-wrap:anywhere]" : "truncate")}>
    <span className="block">{it.label}</span>
    {it.sublabel && <span className="block text-[11px] leading-snug text-zinc-400">{it.sublabel}</span>}
  </span>
);

// Every typed word must appear somewhere in the option text (no fuzzy matches).
const substringFilter = (value, search) => {
  const hay = value.toLowerCase();
  return search.toLowerCase().split(/\s+/).filter(Boolean).every((w) => hay.includes(w)) ? 1 : 0;
};

/**
 * Searchable select dropdown. Accepts either:
 *   - items: Array<{ value: string, label: string, sublabel?: string, group?: string, suffix?: ReactNode }>
 * `wrap` lets long labels wrap (field and options grow taller) instead of truncating.
 *   - or groups: Array<{ label: string, items: Array<{ value, label, suffix? }> }>
 * Renders flat list if `items` is given; renders grouped list if `groups` is given.
 */
export function SearchableSelect({
  value,
  onChange,
  items,
  groups,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyText = "No results.",
  triggerClassName = "",
  popoverClassName = "",
  testId,
  disabled = false,
  size = "default", // "default" | "sm"
  wrap = false,
}) {
  const [open, setOpen] = useState(false);
  const trigClass = cn(
    "rounded-sm justify-between font-normal w-full text-left",
    size === "sm" ? "h-8 text-xs" : "h-9 text-sm",
    wrap && (size === "sm" ? "h-auto min-h-8 py-1.5 whitespace-normal" : "h-auto min-h-9 py-2 whitespace-normal"),
    !value && "text-zinc-400",
    triggerClassName,
  );

  // Find the selected item label across either shape
  let selectedLabel = "";
  if (value) {
    const flat = items || (groups || []).flatMap((g) => g.items || []);
    const hit = flat.find((it) => it.value === value);
    if (hit) selectedLabel = hit.label;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open}
          disabled={disabled} className={trigClass} data-testid={testId}>
          <span className={wrap ? "min-w-0 [overflow-wrap:anywhere] text-left" : "truncate"}>{selectedLabel || placeholder}</span>
          <CaretUpDown size={12} className="ml-2 shrink-0 text-zinc-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("p-0 w-[--radix-popover-trigger-width] min-w-[260px]", popoverClassName)} align="start">
        <Command filter={wrap ? substringFilter : undefined}>
          <CommandInput placeholder={searchPlaceholder} className="h-9" data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList className={wrap ? "max-h-96" : "max-h-72"}>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {items && (
              <CommandGroup>
                {items.map((it) => (
                  <CommandItem
                    key={it.value}
                    value={`${it.label} ${it.searchText || ""}`}
                    onSelect={() => { onChange(it.value); setOpen(false); }}
                    data-testid={testId ? `${testId}-item-${it.value}` : undefined}
                    className="text-sm cursor-pointer"
                  >
                    <Check size={12} className={cn("mr-2", value === it.value ? "opacity-100 text-emerald-500" : "opacity-0")} />
                    <ItemLabel it={it} wrap={wrap} />
                    {it.suffix}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {groups && groups.map((g, gi) => (
              <CommandGroup key={`${g.label}-${gi}`} heading={g.label}>
                {(g.items || []).map((it) => (
                  <CommandItem
                    key={it.value}
                    value={`${g.label} ${it.label} ${it.searchText || ""}`}
                    onSelect={() => { onChange(it.value); setOpen(false); }}
                    data-testid={testId ? `${testId}-item-${it.value}` : undefined}
                    className="text-sm cursor-pointer"
                  >
                    <Check size={12} className={cn("mr-2", value === it.value ? "opacity-100 text-emerald-500" : "opacity-0")} />
                    <ItemLabel it={it} wrap={wrap} />
                    {it.suffix}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
